<script lang="ts" setup>
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import { computed, ref } from 'vue';
import { useGebruikerSearch } from '@services/crm-internal/orval-api/gebruiker-api/gebruiker-api';
import { type CrmGebruikersGetGebruikersLikeStringDto as GebruikerDto } from '@services/crm-internal/orval-api/model/crmGebruikersGetGebruikersLikeStringDto';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import VDataTable from '@shared/component/VDataTable/VDataTable.vue';
import VColumn from '@shared/component/VDataTable/VColumn.vue';
import { usePakkettenGetByPakketId } from '@services/producten-internal/orval-api/pakketten-api/pakketten-api';

const emit = defineEmits<{ close: [] }>();
const beurspakketId = 'SUREBUSINE';

function selectGebruiker(gebruiker: GebruikerDto) {
  const url = `/content/beheer/intern/tussenpersonen/details/algemeen/medewerkers/muteren/?gebruikerid=${gebruiker.id}`;
  const iFrame = document.getElementById('menu-iframe');
  if (iFrame) {
    emit('close');
    (iFrame as HTMLIFrameElement).src = url;
  } else {
    window.location.href = url;
  }
}

const filter = ref('');

const { data: users } = useGebruikerSearch(
  computed(() => ({
    term: filter.value,
  })),
  {
    query: {
      select: (response) => response.result,
      enabled: computed(() => !!filter.value),
    },
  },
);

const { data: beurspakket } = usePakkettenGetByPakketId(beurspakketId, {
  query: {
    select: (response) => response.result,
  },
});

const enrichedUsers = computed(() => {
  if (!users.value) return [];

  return users.value.map((user) => ({
    ...user,
    beursBeschikbaar: user.advisorId
      ? beurspakket.value?.includedAdvisorIds?.includes(user.advisorId)
      : false,
  }));
});

function searchUsers(searchTerm: string) {
  if (searchTerm.trim().length < 3) return;
  filter.value = searchTerm;
}

function cleanTel(telefoonNr: string) {
  return telefoonNr.replace(/-|\s/g, '');
}
</script>

<template>
  <div class="p-8">
    <VSearch
      placeholder="Voer minstens 3 letters in om te zoeken..."
      @input="searchUsers"
      class="mb-8"
    />
    <VDataTable
      :value="enrichedUsers"
      sort-field="volledigeNaam"
      :sort-order="1"
      @row-click="(e: any) => selectGebruiker(e.data)"
      show-row-arrow
      borderless
    >
      <VColumn sortable header="Naam" field="volledigeNaam" />
      <VColumn sortable header="Kantoor" field="advisorName">
        <template #body="{ data }">{{ data.advisorName }} ({{ data.advisorId }})</template>
      </VColumn>
      <VColumn sortable header="Email" field="email">
        <template #body="{ data }">
          <VTooltip :label="`mail naar: ${data.email}`">
            <div class="max-w-52 truncate">
              <a :href="`mailto:${data.email}`" @click.stop>
                {{ data.email }}
              </a>
            </div>
          </VTooltip>
        </template>
      </VColumn>
      <VColumn header="Telefoon" field="telefoon">
        <template #body="{ data }">
          <VTooltip v-if="data.telefoonnummer" :label="`start gesprek: ${data.telefoonnummer}`">
            <a :href="`tel:${cleanTel(data.telefoonnummer)}`" @click.stop>
              {{ data.telefoonnummer }}
            </a>
          </VTooltip>
          <span v-else>-</span>
        </template>
      </VColumn>
      <VColumn sortable header="Accountmanager" field="accountmanager">
        <template #body="{ data }">{{ data.accountmanager ?? '-' }}</template>
      </VColumn>
      <VColumn sortable header="Beurs" field="beursBeschikbaar">
        <template #body="{ data }">{{ data.beursBeschikbaar ? 'Ja' : 'Nee' }}</template>
      </VColumn>
    </VDataTable>
  </div>
</template>
